import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';

interface ChangeRestaurantBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (newBalance: number) => void;
  currentBalance: number;
  loading?: boolean;
}

const ChangeRestaurantBalanceModal: React.FC<ChangeRestaurantBalanceModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  currentBalance,
  loading = false
}) => {
  const { t } = useTranslation();
  const [balance, setBalance] = useState('');

  useEffect(() => {
    if (isOpen) {
      setBalance(currentBalance.toString());
    }
  }, [isOpen, currentBalance]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newBalance = parseFloat(balance);

    if (!isNaN(newBalance) && newBalance >= 0) {
      onSubmit(newBalance);
    }
  };

  const handleClose = () => {
    setBalance(currentBalance.toString());
    onClose();
  };

  const handleBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string, numbers, and one decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setBalance(value);
    }
  };

  const isFormValid = () => {
    const newBalance = parseFloat(balance);
    return !isNaN(newBalance) && newBalance >= 0 && newBalance !== currentBalance;
  };

  const formatPrice = (price: number): string => {
    return `${price.toLocaleString()} soum`;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('modals.changeRestaurantBalance.title')}>
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="modal-info-box" style={{
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>{t('modals.changeRestaurantBalance.currentBalance')}</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#28a745' }}>
            {formatPrice(currentBalance)}
          </div>
        </div>

        <div className="modal-form-group">
          <label htmlFor="balance">{t('modals.changeRestaurantBalance.newBalanceLabel')}</label>
          <input
            type="text"
            id="balance"
            value={balance}
            onChange={handleBalanceChange}
            placeholder={t('modals.changeRestaurantBalance.newBalancePlaceholder')}
            required
            disabled={loading}
          />
          <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
            {t('modals.changeRestaurantBalance.balanceHint')}
          </small>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-button secondary"
            disabled={loading}
          >
            {t('modals.changeRestaurantBalance.cancel')}
          </button>
          <button
            type="submit"
            className="modal-button primary"
            disabled={loading || !isFormValid()}
          >
            {loading ? t('modals.changeRestaurantBalance.updating') : t('modals.changeRestaurantBalance.update')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ChangeRestaurantBalanceModal;
